import { useReducer, useRef, useState } from 'react'
import effects from 'app/effects/effects'
import { z } from 'zod'
import axios from 'axios'
import { copyToClipboard } from 'libs/clipboard'
import MessageAlert from 'app/components/MessageAlert'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { getCreditsRequest } from './api'
import { usePlace } from 'commons/usePlace'

function reduce(state, action) {
  switch (action.type) {
    case 'rate':
      return {
        ...state,
        status: 'loading',
      }

    case 'regenerate':
      return {
        ...state,
        status: 'regenerating',
      }

    case 'close':
      return { ...initialState }

    case 'open-dialog-confirm':
      return { ...state, isCanceled: true }

    case 'close-open-dialog-confirm':
      return { ...state, isCanceled: false }

    case 'on-tone-change':
      return {
        ...state,
        tone: action.tone,
      }

    case 'on-improve-option-change':
      return {
        ...state,
        improveOption: action.improveOption,
      }

    case 'rate-response':
      return {
        ...state,
        status: 'rated',
        rating: action.response.rating,
        feedback: action.response.feedback,
        revisedVersion: action.response.revised_version,
      }

    case 'credits-response':
      return {
        ...state,
        credits: action.credits,
      }

    case 'show-error':
      return {
        ...state,
        status: 'error',
        error: action.error,
      }

    default:
      return state
  }
}

const initialState = {
  status: undefined,
  rating: undefined,
  feedback: undefined,
  revisedVersion: undefined,
  credits: undefined,
  tone: undefined,
  improveOption: undefined,
  error: undefined,
  isCanceled: false,
}

const rateResponseSchema = z.object({
  response: z.object({
    rating: z.number(),
    feedback: z.string().nullish(),
    revised_version: z.string().nullish(),
  }),
})

const doNotShowAgainStorageId = 'com.messagespring.ai.doNotShowAgain'
function useDoNotShowAgainStorage() {
  const [doNotShowAgain, setDoNotShowAgain] = useState(() => {
    return localStorage.getItem(doNotShowAgainStorageId) === 'true'
  })

  return {
    doNotShowAgain,
    onToggleDoNotShowAgain: function () {
      setDoNotShowAgain((doNotShowAgain) => {
        const updated = !doNotShowAgain
        localStorage.setItem(doNotShowAgainStorageId, updated)
        return updated
      })
    },
  }
}

export function useRateWithAI(requestId, onReplaceWithAIVersionClick) {
  const reduxDispatch = useDispatch()
  const [state, dispatch] = useReducer(reduce, initialState)
  const cancellableRef = useRef(new AbortController())
  const { formatMessage } = useIntl()
  const doNotShowAgain = useDoNotShowAgainStorage()
  const place = usePlace()
  const isChanging = place.aiBillingCharged

  return {
    ...state,
    isOpen: state.status !== undefined,
    onRateClick: async function (text) {
      if (!text || text.length <= 0) return
      dispatch({ type: 'rate' })

      cancelInflightRequest(cancellableRef)
      await getCredits(dispatch, cancellableRef)
      await rate({ text }, requestId, dispatch, cancellableRef)
      await getCredits(dispatch, cancellableRef)
    },
    onCloseClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'close' })
    },
    onCancelClick: function () {
      if (isChanging) {
        dispatch({ type: 'open-dialog-confirm' })
      } else {
        cancelInflightRequest(cancellableRef)
        dispatch({ type: 'close' })
      }
    },
    onConfirmClick: () => {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'close' })
    },
    onCloseDialogConfirm: () => {
      dispatch({ type: 'close-open-dialog-confirm' })
    },
    onCopyTextClick: async function () {
      const revisedVersion = state.revisedVersion
        ? state.revisedVersion.trim()
        : undefined
      if (!revisedVersion) return
      await copyToClipboard(revisedVersion)
      reduxDispatch(
        MessageAlert.showSuccess({
          message: formatMessage({
            id: 'text.copied',
          }),
        }),
      )
    },
    onReplaceWithAIVersionClick: function () {
      if (!onReplaceWithAIVersionClick) {
        return
      }

      onReplaceWithAIVersionClick({
        rating: state.rating,
        revisedVersion: state.revisedVersion,
      })
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'close' })
    },
    doNotShowAgain: doNotShowAgain.doNotShowAgain,
    onToggleDoNotShowAgain: doNotShowAgain.onToggleDoNotShowAgain,
    onRegenerateContentClick: async function () {
      dispatch({ type: 'regenerate' })
      cancelInflightRequest(cancellableRef)

      await rate(
        {
          text: state.revisedVersion,
          tone: state.tone,
          type: state.improveOption,
        },
        requestId,
        dispatch,
        cancellableRef,
      )
      await getCredits(dispatch, cancellableRef)
      await getCredits(dispatch, cancellableRef)
    },
    onToneChange: function (tone) {
      dispatch({ type: 'on-tone-change', tone })
    },
    onImproveOptionChange: function (improveOption) {
      dispatch({ type: 'on-improve-option-change', improveOption })
    },
  }
}

async function rate({ text, tone, type }, requestId, dispatch, cancellableRef) {
  try {
    const response = rateResponseSchema.parse(
      await effects.apis.ai.rateMessage({
        data: {
          text,
          tone,
          type,
          request_id: requestId,
        },
        signal: cancellableRef.current.signal,
      }),
    )
    dispatch({ type: 'rate-response', response: response.response })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
  }
}

async function getCredits(dispatch, cancellableRef) {
  try {
    const response = await getCreditsRequest(cancellableRef)
    dispatch({
      type: 'credits-response',
      credits: response.credits,
    })
  } catch (error) {
    if (axios.isCancel(error)) return
  }
}

function cancelInflightRequest(cancellableRef) {
  cancellableRef.current.abort()
  cancellableRef.current = new AbortController()
}
